import React, { useEffect, useState } from 'react';
import {
  ArrowLeftRight,
  BarChart3,
  BookOpen,
  Bot,
  CalendarDays,
  Check,
  SlidersHorizontal,
} from 'lucide-react';
import { auth } from '../firebase';
import { getGoogleRedirectError, getGoogleSignInErrorMessage, isGoogleSignInCancelled, signInWithGoogle } from '../utils/googleSignIn';

const GoogleIcon = () => (
  <svg className="h-5 w-5 shrink-0" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

const SignInButton = ({ className = '', label = 'Start journaling — free', compact = false }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSignIn = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await signInWithGoogle(auth);
      // A redirect navigates away; keep the button busy until it does.
      if (result?.mode !== 'redirect') setLoading(false);
    } catch (signInError) {
      setLoading(false);
      // Closing the Google popup is a cancel, not an error.
      if (isGoogleSignInCancelled(signInError)) return;
      setError(getGoogleSignInErrorMessage(signInError));
    }
  };

  const button = (
    <button
      type="button"
      onClick={handleSignIn}
      disabled={loading}
      className={`inline-flex items-center justify-center gap-2 rounded-xl bg-black px-5 py-3 font-bold text-white transition hover:bg-black/80 disabled:opacity-60 ${className}`}
    >
      <GoogleIcon />
      {loading ? 'Opening Google…' : label}
    </button>
  );

  if (compact) {
    return (
      <div className="relative">
        {button}
        {error && <p role="alert" className="absolute right-0 top-full mt-2 w-64 rounded-lg bg-red-50 px-3 py-2 text-xs font-medium text-red-700 shadow-lg">{error}</p>}
      </div>
    );
  }

  return (
    <>
      {button}
      {error && <p role="alert" className="mt-3 text-sm font-medium text-red-700">{error}</p>}
    </>
  );
};

// Shows why a redirect sign-in (used when popups are blocked) failed after Google sends the user back.
const RedirectSignInError = () => {
  const [message, setMessage] = useState('');
  useEffect(() => {
    let active = true;
    Promise.resolve(getGoogleRedirectError(auth))
      .then((error) => { if (active && error) setMessage(getGoogleSignInErrorMessage(error)); })
      .catch(() => {});
    return () => { active = false; };
  }, []);
  return message ? <p role="alert" className="mt-3 text-sm font-medium text-red-700">{message}</p> : null;
};

const FEATURES = [
  {
    icon: SlidersHorizontal,
    title: 'Journal your way',
    copy: 'Record P&L, charts, and the fields your strategy needs.',
    points: ['Three journal layouts', 'Custom fields + chart uploads'],
  },
  {
    icon: CalendarDays,
    title: 'Calendar + weekly reviews',
    copy: 'See every result and review each week with the numbers ready.',
    points: ['Daily + monthly P&L cards', 'Reviews saved to Notebook'],
  },
  {
    icon: BarChart3,
    title: 'Analytics for every field',
    copy: 'Break down performance by any field you track.',
    points: ['Win rate, expectancy + PF', 'Compare periods + conditions'],
  },
  {
    icon: BookOpen,
    title: 'Connected notebook',
    copy: 'Keep research, lessons, and reviews in one workspace.',
    points: ['Folders + linked notes', 'Automatic review folder'],
  },
  {
    icon: Bot,
    title: 'Export + AI context',
    copy: 'Export your data or prepare focused context for BandzBot.',
    points: ['CSV + JSON exports', 'AI-ready journal history'],
  },
  {
    icon: ArrowLeftRight,
    title: 'Account Activity',
    copy: 'Track cash movement without changing trading performance.',
    points: ['Deposits + payouts', 'Account filters + balances'],
  },
];

const LandingPage = () => (
  <main id="top" className="min-h-screen bg-[#f3f1eb] text-black">
    <nav className="sticky top-0 z-50 border-b border-black/15 bg-[#f3f1eb]/90 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 lg:px-8">
        <a href="#top" className="text-xl font-black tracking-[-0.055em]">BandzJournal</a>
        <div className="hidden items-center gap-7 text-sm font-medium text-black/55 md:flex">
          <a className="hover:text-black" href="#product">Product</a>
          <a className="hover:text-black" href="#features">Features</a>
        </div>
        <SignInButton className="!px-4 !py-2 text-sm" label="Start free" compact />
      </div>
    </nav>

    <section className="px-5 pb-16 pt-16 lg:px-8 lg:pb-24 lg:pt-24">
      <div className="mx-auto max-w-7xl">
        <p className="mb-5 font-mono text-xs font-bold uppercase tracking-[0.22em] text-sky-600">Free trading journal · No subscription</p>
        <h1 className="-ml-[0.045em] max-w-6xl text-[clamp(4rem,9vw,8.5rem)] font-black leading-[0.84] tracking-[-0.075em]">
          Your trades are data.<br /><span className="text-sky-500">Use them.</span>
        </h1>
        <div className="mt-12 grid gap-8 border-t border-black/20 pt-7 md:grid-cols-2 md:items-end">
          <p className="max-w-xl text-xl leading-8 text-black/60">Journal faster, review every week, and turn your trading history into decisions you can actually use.</p>
          <div className="md:text-right">
            <SignInButton />
            <RedirectSignInError />
            <p className="mt-3 text-xs text-black/40">Free to use. Trusted by thousands of traders.</p>
          </div>
        </div>
      </div>
    </section>

    <section id="product" className="bg-black px-5 py-14 text-white lg:px-8 lg:py-16">
      <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-[0.86fr_1.14fr] lg:items-center">
        <div>
          <p className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-sky-400">The week at a glance</p>
          <h2 className="mt-4 max-w-md text-4xl font-black leading-[0.96] tracking-[-0.045em] sm:text-[2.75rem]">Less guessing.<br />More evidence.</h2>
          <p className="mt-5 max-w-sm text-sm leading-6 text-white/50">See P&L, consistency, weekly reviews, and the exact conditions connected to every result.</p>
        </div>
        <div className="overflow-hidden rounded-2xl border border-white/10 bg-[#101214] p-2 shadow-2xl shadow-sky-500/10">
          <img
            src="/images/landing-calendar-dashboard.png"
            alt="Bandz Journal monthly calendar showing daily and weekly trading performance"
            className="block w-full rounded-xl"
          />
        </div>
      </div>
    </section>

    <section id="features" className="border-b border-black/20 px-5 py-10 lg:px-8 lg:py-12">
      <div className="mx-auto max-w-7xl">
        <div className="mb-5 max-w-3xl">
          <p className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-sky-600">BandzJournal at a glance</p>
          <h2 className="mt-2 text-3xl font-black tracking-tight sm:text-4xl">A quick look at what’s inside.</h2>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map(({ icon: Icon, title, copy, points }, index) => (
            <article key={title} className="flex flex-col rounded-2xl border border-black/15 bg-white/45 p-4">
              <div className="flex items-center justify-between"><Icon className="h-4 w-4" /><span className="font-mono text-xs font-bold text-sky-600">0{index + 1}</span></div>
              <h3 className="mt-3 text-lg font-black leading-tight">{title}</h3>
              <p className="mt-1.5 text-[13px] leading-5 text-black/60">{copy}</p>
              <div className="mt-2.5 border-t border-black/15 pt-1.5">
                {points.map((point) => <div key={point} className="flex items-center gap-2 py-0.5 text-[13px] font-semibold leading-5 text-black/65"><Check className="h-3.5 w-3.5 shrink-0 text-sky-600" />{point}</div>)}
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>

    <section id="workflow" className="bg-black px-5 py-20 text-white lg:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="mb-10"><p className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-sky-400">One connected workflow</p><h2 className="mt-4 text-4xl font-black">Journal the trade. Measure the pattern.</h2></div>
        <div className="overflow-hidden rounded-2xl border border-white/10 bg-[#101214] p-2 shadow-2xl shadow-sky-500/10">
          <img
            src="/images/landing-summary-dashboard.png"
            alt="Bandz Journal Summary dashboard showing performance cards and an equity curve"
            className="block w-full rounded-xl"
          />
        </div>
      </div>
    </section>

    <section className="px-5 py-20 text-center lg:px-8">
      <p className="font-mono text-xs font-bold uppercase tracking-widest text-sky-600">100% free · No subscription</p>
      <h2 className="mx-auto mt-4 max-w-3xl text-5xl font-black leading-none tracking-[-0.05em]">Your trading history should make you better.</h2>
      <SignInButton className="mt-8" />
    </section>

    <footer className="border-t border-black/15 px-5 py-7 text-xs text-black/45 lg:px-8">
      <div className="mx-auto flex max-w-7xl justify-between"><span>BandzJournal</span><span>Track it. Review it. Improve it.</span></div>
    </footer>
  </main>
);

export default LandingPage;
